import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminShell from "@/components/admin/AdminShell";
import { Icon } from "@/components/Icon";
import {
  Panel, PageHeader, BtnPrimary, BtnGhost, RowBtn, StatTile, StatGrid,
  Chip, SearchBox, StatusPill, EmptyRow, Loading,
} from "@/components/admin/kit";

const MO = "/casino/admin/market-ops";
const SPORTS = ["esports", "basketball", "football", "baseball", "hockey", "soccer", "tennis", "mma", "golf", "other"];
const TERMINAL = new Set(["settled", "closed", "cancelled", "canceled", "void"]);
const emptyOutcome = () => ({ label: "", odds: "2.00", market: "", line: "", side: "" });
const emptyForm = () => ({
  source: "manual", event_type: "outright", sport: "esports", league: "",
  title: "", home_team: "", away_team: "", start_time: "", max_bet: "",
  one_per_user: false, parent_event_id: "", publish: true,
  source_event_id: "", source_event_name: "",
});

function money(v) {
  const n = Number(v || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function coins(v) {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function sourceLabel(v) {
  return ({ manual: "Manual", upa: "Event import", csv: "CSV", paste: "Paste" })[v] || v || "Manual";
}

function marketName(m) {
  if (m.event_type === "outright" || m.event_type === "prop") return m.title || "Untitled market";
  return `${m.away_team || "Away"} @ ${m.home_team || "Home"}`;
}

function formatStart(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function toDateTimeLocal(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function normalizeSport(raw) {
  const value = String(raw || "").trim().toLowerCase();
  if (SPORTS.includes(value)) return value;
  if (/2k|esport|gaming/.test(value)) return "esports";
  if (/basketball/.test(value)) return "basketball";
  if (/football|nfl/.test(value)) return "football";
  if (/baseball|mlb/.test(value)) return "baseball";
  if (/hockey|nhl/.test(value)) return "hockey";
  if (/soccer|football club/.test(value)) return "soccer";
  if (/tennis/.test(value)) return "tennis";
  if (/mma|ufc/.test(value)) return "mma";
  if (/golf/.test(value)) return "golf";
  return "esports";
}

function unresolvedOutcomes(m) {
  return (m?.outcomes || []).filter((o) => o.sync_state === "removed_upstream_needs_resolution");
}

function displayError(err, fallback = "Request failed") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  return apiError(detail) || fallback;
}

function splitCsvLine(line) {
  const cells = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cell += '"'; i += 1; }
      else quoted = !quoted;
    } else if (ch === "," && !quoted) {
      cells.push(cell.trim()); cell = "";
    } else cell += ch;
  }
  cells.push(cell.trim());
  return cells;
}

function parseParticipantText(text, defaultOdds = 4) {
  const lines = String(text || "").split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
  if (!lines.length) return [];
  const rows = lines.map(splitCsvLine);
  const headers = rows[0].map((x) => x.toLowerCase());
  const hasHeader = headers.some((h) => ["name", "team", "team_name", "participant", "entrant"].includes(h));
  const body = hasHeader ? rows.slice(1) : rows;
  const nameIndex = hasHeader ? Math.max(0, headers.findIndex((h) => ["name", "team", "team_name", "participant", "entrant"].includes(h))) : 0;
  const oddsIndex = hasHeader ? headers.findIndex((h) => ["odds", "price", "decimal_odds"].includes(h)) : 1;
  const idIndex = hasHeader ? headers.findIndex((h) => ["id", "team_id", "participant_id", "source_id"].includes(h)) : 2;
  return body.map((cols, i) => {
    const name = (cols[nameIndex] || "").trim();
    const rawOdds = oddsIndex >= 0 ? Number(cols[oddsIndex]) : defaultOdds;
    return {
      source_id: idIndex >= 0 && cols[idIndex] ? cols[idIndex] : `row-${i + 1}`,
      name,
      odds: Number.isFinite(rawOdds) && rawOdds > 1 ? rawOdds : defaultOdds,
      selected: true,
      status: "approved",
    };
  }).filter((x) => x.name);
}

function Modal({ open, title, subtitle, onClose, wide = false, children, footer }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`max-h-[94vh] w-full overflow-hidden rounded-t-[18px] border border-[#1F1F23] bg-[#0C0C0E] shadow-2xl sm:rounded-[18px] ${wide ? "sm:max-w-5xl" : "sm:max-w-xl"}`}>
        <div className="flex items-start gap-3 border-b border-[#1F1F23] px-4 py-4 sm:px-5">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-[20px] font-extrabold tracking-[-.02em]">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[12px] text-[#71717a]">{subtitle}</p>}
          </div>
          <button className="flex h-9 w-9 items-center justify-center rounded-lg text-[#a1a1aa] hover:bg-white/[.05] hover:text-white" onClick={onClose}><Icon name="x" size={17} /></button>
        </div>
        <div className="max-h-[calc(94vh-132px)] overflow-y-auto px-4 py-4 sm:px-5">{children}</div>
        {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-[#1F1F23] px-4 py-3 sm:px-5">{footer}</div>}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[.12em] text-[#71717a]">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-[#52525b]">{hint}</span>}
    </label>
  );
}

const inputClass = "h-10 w-full rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 text-[13px] font-medium text-white outline-none placeholder:text-[#52525b] focus:ring-1 focus:ring-[#00D632]/40";
const selectClass = `${inputClass} appearance-none`;
const textAreaClass = "min-h-[112px] w-full rounded-[10px] border border-[#1F1F23] bg-[#141417] px-3 py-2.5 text-[13px] font-medium text-white outline-none placeholder:text-[#52525b] focus:ring-1 focus:ring-[#00D632]/40";

export default function MarketOperations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [sourceStatus, setSourceStatus] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [settleMarket, setSettleMarket] = useState(null);
  const [syncMarket, setSyncMarket] = useState(null);
  const [priceMarket, setPriceMarket] = useState(null);
  const [resolveMarket, setResolveMarket] = useState(null);

  const load = async () => {
    try {
      const [{ data }, statusRes] = await Promise.all([
        api.get(`${MO}/markets`),
        api.get(`${MO}/source-status`).catch(() => ({ data: null })),
      ]);
      setMarkets(data.markets || []);
      setSourceStatus(statusRes.data);
    } catch (e) {
      toast.error(displayError(e, "Could not load markets"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => ({
    all: markets.length,
    open: markets.filter((m) => m.status === "open").length,
    draft: markets.filter((m) => m.status === "draft").length,
    paused: markets.filter((m) => m.status === "paused").length,
    upa: markets.filter((m) => m.source_system === "upa").length,
  }), [markets]);

  const shown = useMemo(() => markets.filter((m) => {
    if (filter === "upa" && m.source_system !== "upa") return false;
    if (filter !== "all" && filter !== "upa" && m.status !== filter) return false;
    if (query.trim()) {
      const hay = `${marketName(m)} ${m.league || ""} ${m.sport || ""} ${m.source_event_name || ""}`.toLowerCase();
      if (!hay.includes(query.trim().toLowerCase())) return false;
    }
    return true;
  }), [markets, filter, query]);

  const togglePause = async (m) => {
    try {
      if (m.status === "draft") await api.post(`${MO}/markets/${m.id}/publish`);
      else await api.post(`/events/${m.id}/${m.status === "paused" ? "resume" : "pause"}`);
      toast.success(m.status === "draft" ? "Market published" : m.status === "paused" ? "Market resumed" : "Market paused");
      await load();
    } catch (e) { toast.error(displayError(e)); }
  };

  const handleAdminNav = (key) => {
    if (key === "markets") return;
    navigate(`/admin?section=${encodeURIComponent(key)}`);
  };

  return (
    <AdminShell active="markets" onChange={handleAdminNav} badges={{}} user={user} title="Market Operations">
      <PageHeader
        title="Market Operations"
        meta="Manual, imported, promotional, and partner markets — one betting and settlement path."
        actions={<><BtnGhost onClick={load}><Icon name="refresh" size={14} /> Refresh</BtnGhost><BtnPrimary onClick={() => setCreateOpen(true)} data-testid="market-create-open"><Icon name="plus" size={14} /> Create Market</BtnPrimary></>}
      />

      <StatGrid cols={5}>
        <StatTile icon="sportsbook" label="CUSTOM MARKETS" value={counts.all} />
        <StatTile icon="sportsbook" label="OPEN" value={counts.open} valueColor="#00D632" />
        <StatTile icon="audit" label="DRAFT" value={counts.draft} />
        <StatTile icon="sportsbook" label="PAUSED" value={counts.paused} />
        <StatTile icon="transfer" label="IMPORTED" value={counts.upa} />
      </StatGrid>

      <Panel className="flex flex-wrap items-center gap-3 bg-[#111114] px-3.5 py-3">
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${sourceStatus?.upa?.configured ? "bg-[#00D632]/10 text-[#00D632]" : "bg-[#F59E0B]/10 text-[#F59E0B]"}`}><Icon name="transfer" size={15} /></div>
        <div className="min-w-0 flex-1">
          <div className="text-[12px] font-bold text-white">Event import {sourceStatus?.upa?.configured ? "connected" : "ready for configuration"}</div>
          <div className="truncate text-[11px] text-[#71717a]">{sourceStatus?.upa?.configured ? "Connected" : "Configure the import source on the backend. Manual / CSV / Paste imports work now."}</div>
        </div>
      </Panel>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip active={filter === "all"} onClick={() => setFilter("all")}>All {counts.all}</Chip>
        <Chip active={filter === "open"} onClick={() => setFilter("open")}>Open {counts.open}</Chip>
        <Chip active={filter === "draft"} onClick={() => setFilter("draft")}>Draft {counts.draft}</Chip>
        <Chip active={filter === "paused"} onClick={() => setFilter("paused")}>Paused {counts.paused}</Chip>
        <Chip active={filter === "upa"} onClick={() => setFilter("upa")}>Imported {counts.upa}</Chip>
        <SearchBox className="ml-auto w-full sm:w-[260px]" placeholder="Search markets…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {loading ? <Panel><Loading>Loading market operations…</Loading></Panel> : (
        <div className="grid gap-3">
          {shown.length === 0 ? <Panel><EmptyRow>No markets match this view.</EmptyRow></Panel> : shown.map((m) => {
            const unresolved = unresolvedOutcomes(m);
            const terminal = TERMINAL.has(String(m.status || "").toLowerCase());
            return (
              <Panel key={m.id} className="bg-[#111114] p-3.5" data-testid={`market-row-${m.id}`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <StatusPill status={String(m.status || "open").toUpperCase()} />
                      <span className="truncate font-display text-[15px] font-extrabold">{marketName(m)}</span>
                      <span className="rounded-md bg-white/[.05] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[.08em] text-[#a1a1aa]">{sourceLabel(m.market_origin)}</span>
                      {m.source_system === "upa" && <span className="rounded-md bg-[#00D632]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#00D632]">IMPORTED</span>}
                      {unresolved.length > 0 && <span className="rounded-md bg-[#EF4444]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#EF4444]">{unresolved.length} NEED RESOLUTION</span>}
                    </div>
                    <div className="mt-1 text-[11px] font-medium text-[#71717a]">{m.sport} · {m.league} · {formatStart(m.start_time)} · {(m.outcomes || []).length} outcomes</div>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div><div className="text-[10px] uppercase tracking-[.1em] text-[#52525b]">Bets</div><div className="font-display text-[15px] font-extrabold">{m.market_ops?.bet_count || 0}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[.1em] text-[#52525b]">Cash</div><div className="font-display text-[15px] font-extrabold">{money(m.market_ops?.cash_handle)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-[.1em] text-[#52525b]">Coins</div><div className="font-display text-[15px] font-extrabold">{coins(m.market_ops?.coin_handle)}</div></div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5 border-t border-[#1F1F23] pt-3">
                  {(m.status === "open" || m.status === "paused" || m.status === "draft") && <RowBtn onClick={() => togglePause(m)}>{m.status === "draft" ? "Publish" : m.status === "paused" ? "Resume" : "Pause"}</RowBtn>}
                  {!terminal && <RowBtn onClick={() => setPriceMarket(m)}>Edit odds</RowBtn>}
                  {m.source_system === "upa" && !terminal && <RowBtn onClick={() => setSyncMarket(m)}>Sync roster</RowBtn>}
                  {unresolved.length > 0 && !terminal && <RowBtn onClick={() => setResolveMarket(m)}>Resolve {unresolved.length}</RowBtn>}
                  {!terminal && unresolved.length === 0 && <RowBtn tone="green" onClick={() => setSettleMarket(m)}>Settle safely</RowBtn>}
                  <RowBtn onClick={() => window.open(m.event_type === "outright" ? `/event/${m.id}/outright` : `/event/${m.id}`, "_blank")}>Customer view</RowBtn>
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      <CreateMarketModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={async () => { setCreateOpen(false); await load(); }} sourceStatus={sourceStatus} />
      <SettlementModal market={settleMarket} onClose={() => setSettleMarket(null)} onSettled={async () => { setSettleMarket(null); await load(); }} />
      <SyncModal market={syncMarket} onClose={() => setSyncMarket(null)} onSynced={async () => { setSyncMarket(null); await load(); }} />
      <BulkOddsModal market={priceMarket} onClose={() => setPriceMarket(null)} onSaved={async () => { setPriceMarket(null); await load(); }} />
      <ResolutionModal market={resolveMarket} user={user} onClose={() => setResolveMarket(null)} onChanged={async () => { await load(); }} />
    </AdminShell>
  );
}

function CreateMarketModal({ open, onClose, onCreated, sourceStatus }) {
  const [form, setForm] = useState(emptyForm);
  const [participants, setParticipants] = useState([]);
  const [outcomes, setOutcomes] = useState([emptyOutcome(), emptyOutcome()]);
  const [bulkOdds, setBulkOdds] = useState("4.00");
  const [paste, setPaste] = useState("");
  const [upaEvents, setUpaEvents] = useState([]);
  const [upaBusy, setUpaBusy] = useState(false);
  const [parentEvents, setParentEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [review, setReview] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm()); setParticipants([]); setOutcomes([emptyOutcome(), emptyOutcome()]); setBulkOdds("4.00"); setPaste(""); setReview(null);
    api.get("/events").then(({ data }) => setParentEvents((data || []).filter((e) => e.event_type === "match" && ["open", "paused", "live"].includes(e.status)))).catch(() => setParentEvents([]));
  }, [open]);

  const patch = (key, value) => { setForm((p) => ({ ...p, [key]: value })); setReview(null); };
  const selectedParticipants = participants.filter((p) => p.selected);

  const chooseSource = async (source) => {
    setForm((p) => ({ ...p, source, event_type: source === "upa" ? "outright" : p.event_type }));
    setParticipants([]); setReview(null);
    if (source === "upa" && sourceStatus?.upa?.configured && upaEvents.length === 0) {
      setUpaBusy(true);
      try { const { data } = await api.get(`${MO}/upa/events`); setUpaEvents(data.events || []); }
      catch (e) { toast.error(displayError(e)); }
      finally { setUpaBusy(false); }
    }
  };

  const selectUpaEvent = async (id) => {
    const ev = upaEvents.find((x) => String(x.id) === String(id));
    if (!ev) return;
    setReview(null);
    setForm((p) => ({
      ...p, source_event_id: String(ev.id), source_event_name: ev.title,
      title: `${ev.title} — Tournament Winner`, sport: normalizeSport(ev.sport), league: ev.league || "",
      start_time: ev.start_time ? toDateTimeLocal(ev.start_time) : p.start_time,
    }));
    setUpaBusy(true);
    try {
      const { data } = await api.get(`${MO}/upa/events/${encodeURIComponent(ev.id)}/participants`);
      setParticipants((data.participants || []).map((p) => ({
        source_id: p.id, name: p.name, odds: Number(bulkOdds) || 4,
        selected: p.eligible === true, status: p.status,
      })));
      toast.success(`${data.eligible_count || 0} eligible teams loaded${data.review_count ? ` · ${data.review_count} need review` : ""}`);
    } catch (e) { toast.error(displayError(e)); }
    finally { setUpaBusy(false); }
  };

  const applyText = () => {
    const rows = parseParticipantText(paste, Number(bulkOdds) || 4);
    if (!rows.length) { toast.error("Add at least one team or participant"); return; }
    setParticipants(rows); setReview(null);
    toast.success(`${rows.length} entrants loaded`);
  };

  const readCsv = async (file) => {
    if (!file) return;
    const text = await file.text();
    setPaste(text);
    const rows = parseParticipantText(text, Number(bulkOdds) || 4);
    setParticipants(rows); setReview(null);
    toast.success(`${rows.length} entrants loaded from ${file.name}`);
  };

  const applyBulk = () => {
    const odds = Number(bulkOdds);
    if (!(odds > 1)) { toast.error("Enter decimal odds greater than 1.00"); return; }
    setParticipants((rows) => rows.map((r) => ({ ...r, odds })));
    setOutcomes((rows) => rows.map((r) => ({ ...r, odds: String(odds) })));
    setReview(null);
  };

  const addParticipant = () => { setParticipants((p) => [...p, { source_id: `manual-${Date.now()}`, name: "", odds: Number(bulkOdds) || 4, selected: true, status: "approved" }]); setReview(null); };
  const updateParticipant = (i, key, value) => { setParticipants((rows) => rows.map((r, x) => x === i ? { ...r, [key]: value } : r)); setReview(null); };
  const updateOutcome = (i, key, value) => { setOutcomes((rows) => rows.map((r, x) => x === i ? { ...r, [key]: value } : r)); setReview(null); };

  const payload = () => ({
    ...form,
    start_time: form.start_time ? new Date(form.start_time).toISOString() : "",
    max_bet: form.max_bet ? Number(form.max_bet) : null,
    participants: form.event_type === "outright" ? selectedParticipants.map((p) => ({ ...p, odds: Number(p.odds) })) : [],
    outcomes: form.event_type !== "outright" ? outcomes.filter((o) => o.label.trim()).map((o) => ({
      ...o, market: form.event_type === "prop" ? "prop" : (o.market || "moneyline"),
      odds: Number(o.odds), line: o.line === "" ? null : Number(o.line),
    })) : [],
  });

  const preview = async () => {
    try { const { data } = await api.post(`${MO}/preview`, payload()); setReview(data); }
    catch (e) { toast.error(displayError(e)); }
  };

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${MO}/markets`, payload());
      toast.success(`${data.market?.status === "draft" ? "Draft" : "Market"} created`);
      onCreated();
    } catch (e) { toast.error(displayError(e)); }
    finally { setBusy(false); }
  };

  const outright = form.event_type === "outright";
  return (
    <Modal open={open} onClose={onClose} wide title="Create Market" subtitle="Build manual markets or import entrants into the existing PrymeBet betting path."
      footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost>{review ? <BtnPrimary disabled={busy} onClick={create}>{busy ? "Creating…" : form.publish ? "Create & Publish" : "Save Draft"}</BtnPrimary> : <BtnPrimary onClick={preview}>Review Market</BtnPrimary>}</>}>
      <div className="space-y-5">
        <section>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[.13em] text-[#71717a]">1 · Source</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {["manual", "upa", "csv", "paste"].map((s) => <button key={s} onClick={() => chooseSource(s)} className={`rounded-xl border px-3 py-3 text-left ${form.source === s ? "border-[#00D632] bg-[#00D632]/10" : "border-[#1F1F23] bg-[#141417]"}`}>
              <div className="text-[13px] font-bold text-white">{sourceLabel(s)}</div><div className="mt-0.5 text-[10px] text-[#71717a]">{s === "manual" ? "Build by hand" : s === "upa" ? "Registered teams" : s === "csv" ? "Upload a roster" : "Paste a list"}</div>
            </button>)}
          </div>
          {form.source === "upa" && !sourceStatus?.upa?.configured && <div className="mt-2 rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/8 px-3 py-2 text-[12px] text-[#FBBF24]">Event import is not configured on this environment yet. Manual / CSV / Paste remain available.</div>}
        </section>

        {form.source === "upa" && sourceStatus?.upa?.configured && <section>
          <Field label="Event"><select className={selectClass} value={form.source_event_id} onChange={(e) => selectUpaEvent(e.target.value)} disabled={upaBusy}>
            <option value="">{upaBusy ? "Loading…" : "Select an event"}</option>
            {upaEvents.map((e) => <option key={e.id} value={e.id}>{e.title}{e.registration_count ? ` · ${e.registration_count} entries` : ""}</option>)}
          </select></Field>
        </section>}

        <section>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[.13em] text-[#71717a]">2 · Market type</div>
          <div className="grid grid-cols-3 gap-2">
            {[["match","Match"],["outright","Outright"],["prop","Player Prop"]].map(([key, label]) => <button key={key} disabled={form.source === "upa" && key !== "outright"} onClick={() => patch("event_type", key)} className={`rounded-xl border px-3 py-2.5 text-[12px] font-bold disabled:opacity-35 ${form.event_type === key ? "border-[#00D632] bg-[#00D632]/10 text-[#00D632]" : "border-[#1F1F23] bg-[#141417] text-[#d4d4d8]"}`}>{label}</button>)}
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          <Field label="Sport"><select className={selectClass} value={form.sport} onChange={(e) => patch("sport", e.target.value)}>{SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="League"><input className={inputClass} value={form.league} onChange={(e) => patch("league", e.target.value)} placeholder="e.g. Pro Series" /></Field>
          {form.event_type === "match" ? <>
            <Field label="Away team"><input className={inputClass} value={form.away_team} onChange={(e) => patch("away_team", e.target.value)} /></Field>
            <Field label="Home team"><input className={inputClass} value={form.home_team} onChange={(e) => patch("home_team", e.target.value)} /></Field>
          </> : <div className="sm:col-span-2"><Field label={form.event_type === "prop" ? "Player prop title" : "Market title"}><input className={inputClass} value={form.title} onChange={(e) => patch("title", e.target.value)} placeholder={form.event_type === "outright" ? "Championship — Winner" : "Player — Stat"} /></Field></div>}
          {form.event_type === "prop" && <div className="sm:col-span-2"><Field label="Attach to game (optional)" hint="Linked props appear under the existing game's sportsbook experience."><select className={selectClass} value={form.parent_event_id} onChange={(e) => patch("parent_event_id", e.target.value)}><option value="">Standalone player prop</option>{parentEvents.map((e) => <option key={e.id} value={e.id}>{e.away_team} @ {e.home_team} · {e.league}</option>)}</select></Field></div>}
          <Field label="Start / close time"><input className={inputClass} type="datetime-local" value={form.start_time} onChange={(e) => patch("start_time", e.target.value)} /></Field>
          <Field label="Max bet ($)" hint="Leave blank to use normal risk limits."><input className={inputClass} type="number" min="0" step="1" value={form.max_bet} onChange={(e) => patch("max_bet", e.target.value)} placeholder="50" /></Field>
        </section>

        <section className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-0 flex-1"><div className="text-[13px] font-bold">{outright ? "Entrants" : "Outcomes & odds"}</div><div className="text-[11px] text-[#71717a]">{outright ? `${selectedParticipants.length} selected · ${participants.length} loaded` : `${outcomes.length} rows`}</div></div>
            <input className="h-9 w-24 rounded-lg border border-[#1F1F23] bg-[#0C0C0E] px-2 text-[12px]" type="number" step="0.01" min="1.01" value={bulkOdds} onChange={(e) => setBulkOdds(e.target.value)} />
            <RowBtn tone="green" onClick={applyBulk}>Apply odds to all</RowBtn>
          </div>

          {outright && (form.source === "paste" || form.source === "csv") && <div className="mt-3">
            <textarea className={textAreaClass} value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={'Team Alpha,4.00,team-1\nTeam Bravo,4.00,team-2\n\nHeaders also supported: name,odds,id'} />
            <div className="mt-2 flex flex-wrap gap-2"><RowBtn tone="green" onClick={applyText}>Load pasted entrants</RowBtn>{form.source === "csv" && <label className="cursor-pointer rounded-lg bg-[#18181C] px-[9px] py-1.5 text-[11px] font-semibold text-white shadow-[inset_0_0_0_1px_#26262B]">Choose CSV<input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => readCsv(e.target.files?.[0])} /></label>}</div>
          </div>}

          {outright ? <div className="mt-3 max-h-[340px] overflow-y-auto rounded-xl border border-[#1F1F23]">
            {participants.length === 0 ? <div className="px-3 py-8 text-center text-[12px] text-[#52525b]">Import, paste a roster, upload CSV, or add entrants manually.</div> : participants.map((p, i) => <div key={`${p.source_id}-${i}`} className={`grid grid-cols-[28px_minmax(0,1fr)_88px_28px] items-center gap-2 border-t border-[#1A1A1E] px-2 py-2 first:border-0 ${p.selected ? "" : "opacity-60"}`}>
              <input type="checkbox" checked={p.selected} onChange={(e) => updateParticipant(i, "selected", e.target.checked)} className="h-4 w-4 accent-[#00D632]" />
              <div><input className="h-8 w-full rounded-lg bg-[#0C0C0E] px-2 text-[12px] outline-none ring-1 ring-[#1F1F23] focus:ring-[#00D632]/40" value={p.name} onChange={(e) => updateParticipant(i, "name", e.target.value)} /><div className={`mt-0.5 truncate text-[9px] ${p.status && !["approved", "paid", "confirmed", "registered", "active", "eligible"].includes(String(p.status).toLowerCase()) ? "text-[#F59E0B]" : "text-[#52525b]"}`}>{p.source_id || "manual"}{p.status ? ` · ${p.status}` : ""}</div></div>
              <input className="h-8 w-full rounded-lg bg-[#0C0C0E] px-2 text-right text-[12px] outline-none ring-1 ring-[#1F1F23]" type="number" step="0.01" min="1.01" value={p.odds} onChange={(e) => updateParticipant(i, "odds", e.target.value)} />
              <button onClick={() => { setParticipants((rows) => rows.filter((_, x) => x !== i)); setReview(null); }} className="text-[#71717a] hover:text-[#EF4444]"><Icon name="x" size={14} /></button>
            </div>)}
          </div> : <div className="mt-3 space-y-2">{outcomes.map((o, i) => <div key={i} className="grid grid-cols-[minmax(0,1fr)_92px_30px] gap-2"><input className={inputClass} value={o.label} onChange={(e) => updateOutcome(i, "label", e.target.value)} placeholder={form.event_type === "prop" ? "Over 6.5" : "Outcome"} /><input className={inputClass} type="number" step="0.01" min="1.01" value={o.odds} onChange={(e) => updateOutcome(i, "odds", e.target.value)} /><button onClick={() => { setOutcomes((rows) => rows.filter((_, x) => x !== i)); setReview(null); }} className="text-[#71717a] hover:text-[#EF4444]"><Icon name="x" size={14} /></button></div>)}</div>}
          <button onClick={outright ? addParticipant : () => { setOutcomes((r) => [...r, emptyOutcome()]); setReview(null); }} className="mt-2 text-[11px] font-bold text-[#00D632]">+ Add {outright ? "entrant" : "outcome"}</button>
        </section>

        <section className="grid gap-2 sm:grid-cols-2">
          <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-[#1F1F23] bg-[#141417] px-3 py-3"><input type="checkbox" className="h-4 w-4 accent-[#00D632]" checked={form.one_per_user} onChange={(e) => patch("one_per_user", e.target.checked)} /><span><span className="block text-[12px] font-bold">One bet per player</span><span className="block text-[10px] text-[#71717a]">Server-enforced on this market.</span></span></label>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-[#1F1F23] bg-[#141417] px-3 py-3"><input type="checkbox" className="h-4 w-4 accent-[#00D632]" checked={form.publish} onChange={(e) => patch("publish", e.target.checked)} /><span><span className="block text-[12px] font-bold">Publish immediately</span><span className="block text-[10px] text-[#71717a]">Turn off to save a private draft.</span></span></label>
        </section>

        {review && <section className="rounded-xl border border-[#00D632]/25 bg-[#00D632]/[.045] p-3.5">
          <div className="flex items-center gap-2 text-[13px] font-bold text-[#00D632]"><Icon name="check-circle" size={15} /> Ready to {form.publish ? "publish" : "save"}</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-[#a1a1aa] sm:grid-cols-4"><div>Type<br/><b className="text-white">{form.event_type}</b></div><div>Source<br/><b className="text-white">{sourceLabel(form.source)}</b></div><div>Outcomes<br/><b className="text-white">{review.outcome_count}</b></div><div>Max bet<br/><b className="text-white">{form.max_bet ? money(form.max_bet) : "Risk default"}</b></div></div>
          {(review.warnings || []).map((w) => <div key={w} className="mt-2 text-[11px] text-[#FBBF24]">• {w}</div>)}
        </section>}
      </div>
    </Modal>
  );
}

function SettlementModal({ market, onClose, onSettled }) {
  const [winner, setWinner] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pausedByModal, setPausedByModal] = useState(false);
  // Monotonic request token — any preview/prepare response whose token is not the
  // latest is ignored. Selecting winner A then B must never let A's slower response
  // overwrite the reviewed state while winner = B.
  const reqRef = useRef(0);
  useEffect(() => {
    reqRef.current += 1; // invalidate any in-flight request when the market changes
    setWinner(""); setPreview(null); setPreviewing(false); setConfirmText(""); setPausedByModal(false);
  }, [market?.id]);
  if (!market) return null;
  const winnerOptions = (market.outcomes || []).filter((o) => o.active !== false && o.suspended !== true);
  const loadPreview = async (id) => {
    // New selection → bump the token, drop any stale preview, and clear the typed
    // confirmation so a reviewed-state change can never be confirmed by accident.
    const token = (reqRef.current += 1);
    setWinner(id); setPreview(null); setConfirmText("");
    if (!id) { setPreviewing(false); return; }
    setPreviewing(true);
    try {
      // v3 safety gate: pause/prepare the market before previewing so no wager can land
      // between reviewing the totals and settling. prepare-settlement is a no-op if already paused.
      if (market.status !== "paused") {
        await api.post(`${MO}/markets/${market.id}/prepare-settlement`);
        if (reqRef.current !== token) return; // superseded while pausing
        setPausedByModal(true);
      }
      const { data } = await api.get(`${MO}/markets/${market.id}/settlement-preview`, { params: { winning_outcome_id: id } });
      if (reqRef.current !== token) return; // a newer selection won — discard this response
      setPreview(data);
    } catch (e) {
      if (reqRef.current === token) toast.error(displayError(e));
    } finally {
      if (reqRef.current === token) setPreviewing(false);
    }
  };
  // Confirm is only valid when the loaded preview is for the exact current winner.
  const previewMatchesWinner = Boolean(preview && winner && String(preview.winner?.id) === String(winner));
  const settle = async () => {
    if (!previewMatchesWinner) { toast.error("Wait for the preview of the selected winner to finish"); return; }
    if (confirmText !== "SETTLE") { toast.error("Type SETTLE to confirm"); return; }
    setBusy(true);
    try { await api.post(`/events/${market.id}/settle`, { winning_outcome_id: winner }); toast.success("Market settled and payouts processed"); onSettled(); }
    catch (e) { toast.error(displayError(e)); }
    finally { setBusy(false); }
  };
  const cancel = () => {
    // Opening the preview pauses the market server-side. Make that consequence explicit
    // on Cancel: warn the operator the market is left paused and refresh the board row.
    reqRef.current += 1; // invalidate anything in flight
    if (pausedByModal && market.status !== "paused") {
      toast.warning("Market left PAUSED for settlement — resume it from the board if you are not settling.");
      onSettled(); // closes the modal and reloads the underlying row so its status is current
    } else {
      onClose();
    }
  };
  return <Modal open title="Settlement review" subtitle={marketName(market)} onClose={cancel} footer={<><BtnGhost onClick={cancel}>Cancel</BtnGhost><BtnPrimary disabled={!previewMatchesWinner || confirmText !== "SETTLE" || busy} onClick={settle}>{busy ? "Settling…" : "Confirm settlement"}</BtnPrimary></>}>
    <div className="space-y-4"><Field label="Winning outcome"><select className={selectClass} value={winner} onChange={(e) => loadPreview(e.target.value)}><option value="">Select the verified winner</option>{winnerOptions.map((o) => <option key={o.id} value={o.id}>{o.label} · {Number(o.odds).toFixed(2)}</option>)}</select></Field>
    {(pausedByModal || market.status === "paused") && <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/[.06] px-3 py-2 text-[11px] text-[#FBBF24]">This market is paused for settlement — no new wagers can land. If you cancel without settling, resume it from the board.</div>}
    {previewing && !preview && <div className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3.5 text-[12px] text-[#a1a1aa]">Preparing settlement preview for the selected winner…</div>}
    {preview && previewMatchesWinner && <div className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3.5"><div className="text-[11px] uppercase tracking-[.12em] text-[#71717a]">Payout preview</div><div className="mt-2 font-display text-[18px] font-extrabold text-[#00D632]">{preview.winner?.label}</div><div className="mt-3 grid grid-cols-2 gap-3 text-[11px] sm:grid-cols-3"><div>Direct bets<br/><b className="text-white">{preview.direct_bets}</b></div><div>Winning tickets<br/><b className="text-white">{preview.winning_tickets}</b></div><div>Losing tickets<br/><b className="text-white">{preview.losing_tickets}</b></div><div>Cash handle<br/><b className="text-white">{money(preview.cash_handle)}</b></div><div>Cash payout<br/><b className="text-white">{money(preview.cash_direct_payout)}</b></div><div>Coin payout<br/><b className="text-white">{coins(preview.coin_direct_payout)}</b></div><div>Pending parlays<br/><b className="text-white">{preview.pending_parlays_with_market}</b></div></div><p className="mt-3 text-[10px] text-[#71717a]">{preview.note}</p></div>}
    {preview && previewMatchesWinner && <Field label="Final confirmation" hint="This uses PrymeBet's existing hardened settlement path. Type SETTLE exactly."><input className={inputClass} value={confirmText} onChange={(e) => setConfirmText(e.target.value.toUpperCase())} placeholder="SETTLE" /></Field>}</div>
  </Modal>;
}

function SyncModal({ market, onClose, onSynced }) {
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false); const [ack, setAck] = useState(false);
  useEffect(() => { setData(null); setAck(false); if (!market) return; setBusy(true); api.get(`${MO}/markets/${market.id}/sync-preview`).then(({ data }) => setData(data)).catch((e) => toast.error(displayError(e))).finally(() => setBusy(false)); }, [market?.id]);
  if (!market) return null;
  const apply = async () => { setBusy(true); try { const { data: result } = await api.post(`${MO}/markets/${market.id}/sync`, { apply: true, acknowledge_money: ack }); if (result.unresolved?.length) toast.warning(`${result.unresolved.length} removed entrant(s) were suspended because wagers exist — resolve them before settlement.`); else toast.success("Roster synced"); onSynced(); } catch (e) { toast.error(displayError(e)); } finally { setBusy(false); } };
  const d = data?.diff;
  return <Modal open title="Sync event roster" subtitle={market.source_event_name || marketName(market)} onClose={onClose} footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary disabled={!data || busy || (data.money_on_market && d?.removed?.length > 0 && !ack)} onClick={apply}>{busy ? "Checking…" : "Apply reviewed changes"}</BtnPrimary></>}>
    {busy && !data ? <Loading>Comparing registrations…</Loading> : d ? <div className="space-y-4"><div className="grid grid-cols-2 gap-2 sm:grid-cols-5">{[["Added",d.summary.added,"#00D632"],["Restored",d.summary.restored || 0,"#22C55E"],["Renamed",d.summary.renamed,"#E4E4E7"],["Removed",d.summary.removed,"#EF4444"],["Same",d.summary.unchanged,"#71717a"]].map(([l,v,c]) => <div key={l} className="rounded-xl bg-[#141417] p-3"><div className="text-[10px] text-[#71717a]">{l}</div><div className="font-display text-[22px] font-extrabold" style={{color:c}}>{v}</div></div>)}</div>
      {data.unresolved_count > 0 && <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/[.06] p-3 text-[11px] text-[#FBBF24]">This market already has {data.unresolved_count} entrant{data.unresolved_count === 1 ? "" : "s"} awaiting operator resolution.</div>}
      {d.added.length > 0 && <DiffBlock title="Add" rows={d.added.map((x) => x.name)} tone="#00D632" />}
      {(d.restored || []).length > 0 && <DiffBlock title="Restore" rows={d.restored.map((x) => `${x.from} → ${x.to || x.from}`)} tone="#22C55E" />}
      {d.renamed.length > 0 && <DiffBlock title="Rename" rows={d.renamed.map((x) => `${x.from} → ${x.to}`)} />}
      {d.removed.length > 0 && <DiffBlock title="Removed upstream" rows={d.removed.map((x) => x.name)} tone="#EF4444" />}
      {data.money_on_market && d.removed.length > 0 && <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#EF4444]/30 bg-[#EF4444]/[.06] p-3"><input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#EF4444]" checked={ack} onChange={(e) => setAck(e.target.checked)} /><span className="text-[11px] text-[#FCA5A5]"><b className="text-white">Money is already on this market.</b><br/>Removed entrants will not be silently graded. PrymeBet will suspend them and mark them for resolution instead of changing customer tickets automatically. I reviewed this diff.</span></label>}
    </div> : null}
  </Modal>;
}

function DiffBlock({ title, rows, tone = "#d4d4d8" }) { return <div className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3"><div className="mb-2 text-[10px] font-bold uppercase tracking-[.12em]" style={{color:tone}}>{title}</div>{rows.map((r, i) => <div key={`${r}-${i}`} className="border-t border-[#1A1A1E] py-1.5 text-[12px] first:border-0">{r}</div>)}</div>; }

function ResolutionModal({ market, user, onClose, onChanged }) {
  const unresolved = unresolvedOutcomes(market);
  const [outcomeId, setOutcomeId] = useState("");
  const [impact, setImpact] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const impactReqRef = useRef(0);
  useEffect(() => { setOutcomeId(unresolved[0]?.id || ""); setImpact(null); setNote(""); }, [market?.id]);
  useEffect(() => {
    if (!market || !outcomeId) { setImpact(null); return; }
    // Guard against out-of-order impact responses: a slower fetch for a previously
    // selected entrant must not overwrite the impact shown for the current one.
    const token = (impactReqRef.current += 1);
    api.get(`${MO}/markets/${market.id}/participant-impact/${outcomeId}`)
      .then(({ data }) => { if (impactReqRef.current === token) setImpact(data); })
      .catch((e) => { if (impactReqRef.current === token) toast.error(displayError(e)); });
  }, [market?.id, outcomeId]);
  if (!market) return null;
  const current = unresolved.find((o) => String(o.id) === String(outcomeId));
  const superAdmin = user?.role === "super_admin";

  const resolve = async (resolution) => {
    if (!current) return;
    setBusy(true);
    try {
      if (resolution === "eliminated" || resolution === "void") {
        if (!superAdmin) {
          toast.error("Money-moving entrant resolution requires a super admin.");
          return;
        }
        await api.post("/admin/props/grade-outcome", {
          event_id: market.id,
          outcome_id: current.id,
          result: resolution === "eliminated" ? "lose" : "push",
        });
      }
      await api.post(`${MO}/markets/${market.id}/participants/${current.id}/resolution`, { resolution, note: note || null });
      toast.success(resolution === "restore" ? `${current.label} restored` : resolution === "void" ? `${current.label} voided and tickets refunded/pushed` : `${current.label} graded as eliminated`);
      await onChanged?.();
      const remaining = unresolved.filter((o) => String(o.id) !== String(current.id));
      setOutcomeId(remaining[0]?.id || ""); setImpact(null); setNote("");
      if (!remaining.length) onClose();
    } catch (e) { toast.error(displayError(e)); }
    finally { setBusy(false); }
  };

  return <Modal open title="Resolve removed entrants" subtitle={`${marketName(market)} · settle is blocked until these are resolved`} onClose={onClose}>
    <div className="space-y-4">
      <Field label="Entrant awaiting resolution"><select className={selectClass} value={outcomeId} onChange={(e) => { setOutcomeId(e.target.value); setImpact(null); }}><option value="">Select entrant</option>{unresolved.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}</select></Field>
      {current && <div className="rounded-xl border border-[#EF4444]/25 bg-[#EF4444]/[.04] p-3.5">
        <div className="text-[13px] font-bold text-white">{current.label}</div>
        <div className="mt-1 text-[11px] text-[#FCA5A5]">The source no longer lists this entrant as eligible. PrymeBet suspended the selection instead of silently deciding customer tickets.</div>
      </div>}
      {impact && <div className="grid grid-cols-2 gap-2 sm:grid-cols-4"><MiniStat label="Bets" value={impact.bet_count} /><MiniStat label="Pending" value={impact.pending_bet_count} /><MiniStat label="Cash handle" value={money(impact.cash_handle)} /><MiniStat label="Parlays" value={impact.parlay_count} /></div>}
      <Field label="Operator note (optional)"><textarea className={textAreaClass} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Withdrawal before play, tournament elimination, registration corrected…" /></Field>
      {!superAdmin && impact?.has_money && <div className="rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/[.06] p-3 text-[11px] text-[#FBBF24]">You can restore an entrant, but grading or refunding money-bearing tickets requires a super admin because it uses the existing sportsbook money path.</div>}
      <div className="grid gap-2 sm:grid-cols-3">
        <ResolutionAction title="Eliminated" text="Entrant played and lost. Pending tickets on this selection become losses." disabled={busy || !current || !superAdmin} onClick={() => resolve("eliminated")} />
        <ResolutionAction title="Did not play / Void" text="Entrant never participated. Pending tickets and parlay legs are pushed/refunded." disabled={busy || !current || !superAdmin} onClick={() => resolve("void")} />
        <ResolutionAction title="Restore entrant" text="The source data was wrong or the entrant was reinstated. Reopen this selection." disabled={busy || !current} onClick={() => resolve("restore")} />
      </div>
    </div>
  </Modal>;
}

function MiniStat({ label, value }) { return <div className="rounded-xl bg-[#141417] p-3"><div className="text-[9px] font-bold uppercase tracking-[.1em] text-[#52525b]">{label}</div><div className="mt-1 font-display text-[15px] font-extrabold text-white">{value}</div></div>; }
function ResolutionAction({ title, text, disabled, onClick }) { return <button disabled={disabled} onClick={onClick} className="rounded-xl border border-[#1F1F23] bg-[#141417] p-3 text-left transition-colors hover:bg-[#1a1a1e] disabled:cursor-not-allowed disabled:opacity-40"><div className="text-[12px] font-bold text-white">{title}</div><div className="mt-1 text-[10px] leading-4 text-[#71717a]">{text}</div></button>; }

function BulkOddsModal({ market, onClose, onSaved }) {
  const [setAll, setSetAll] = useState("4.00");
  const [prices, setPrices] = useState({});
  const [globalApplied, setGlobalApplied] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!market) return;
    const next = {};
    (market.outcomes || []).forEach((o) => { next[String(o.id)] = String(o.odds ?? ""); });
    setPrices(next);
    setSetAll(String(market.sync_default_odds || market.outcomes?.[0]?.odds || "4.00"));
    setGlobalApplied(false);
  }, [market?.id]);
  if (!market) return null;

  const applyAll = () => {
    const n = Number(setAll);
    if (!(n > 1)) { toast.error("Odds must be above 1.00"); return; }
    const next = {};
    (market.outcomes || []).forEach((o) => { next[String(o.id)] = String(n); });
    setPrices(next); setGlobalApplied(true);
  };
  const save = async () => {
    const numeric = {};
    for (const [id, raw] of Object.entries(prices)) {
      const n = Number(raw);
      if (!(n > 1)) { toast.error("Every decimal price must be above 1.00"); return; }
      numeric[id] = n;
    }
    setBusy(true);
    try {
      const body = globalApplied ? { set_all: Number(setAll), prices: {} } : { set_all: null, prices: numeric };
      const { data } = await api.post(`${MO}/markets/${market.id}/bulk-odds`, body);
      toast.success(`${data.changed} outcome${data.changed === 1 ? "" : "s"} repriced`); onSaved();
    } catch (e) { toast.error(displayError(e)); }
    finally { setBusy(false); }
  };

  return <Modal open wide title="Edit odds" subtitle={`${marketName(market)} · ${(market.outcomes || []).length} outcomes`} onClose={onClose} footer={<><BtnGhost onClick={onClose}>Cancel</BtnGhost><BtnPrimary disabled={busy} onClick={save}>{busy ? "Saving…" : "Save odds"}</BtnPrimary></>}>
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-[#1F1F23] bg-[#111114] p-3"><div className="w-32"><Field label="Set all"><input className={inputClass} type="number" min="1.01" step="0.01" value={setAll} onChange={(e) => { setSetAll(e.target.value); setGlobalApplied(false); }} /></Field></div><RowBtn tone="green" onClick={applyAll}>Apply to every outcome</RowBtn><span className="pb-2 text-[10px] text-[#71717a]">Useful for a 4.00 tournament promotion; edit individual rows below when needed.</span></div>
      <div className="max-h-[420px] overflow-y-auto rounded-xl border border-[#1F1F23]">
        {(market.outcomes || []).map((o) => <div key={o.id} className="grid grid-cols-[minmax(0,1fr)_110px] items-center gap-3 border-t border-[#1A1A1E] px-3 py-2 first:border-0"><div className="min-w-0"><div className="truncate text-[12px] font-bold text-white">{o.label}</div><div className="text-[9px] text-[#52525b]">{o.suspended ? "suspended" : o.active === false ? "inactive" : "open"}</div></div><input className={inputClass} type="number" min="1.01" step="0.01" value={prices[String(o.id)] ?? ""} onChange={(e) => { setPrices((p) => ({ ...p, [String(o.id)]: e.target.value })); setGlobalApplied(false); }} /></div>)}
      </div>
      <div className="rounded-xl border border-[#1F1F23] bg-[#111114] p-3 text-[11px] text-[#71717a]">Existing accepted tickets keep the price captured on the ticket. Repricing changes the current offer and increments its outcome revision.</div>
    </div>
  </Modal>;
}
